import GjsEditor, { AssetsProvider, Canvas, ModalProvider, useEditor, WithEditor } from '@grapesjs/react';
import { Box, Paper } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import type { EditorConfig } from 'grapesjs';
import grapesjs, { usePlugin } from 'grapesjs';
import { useState } from 'react';
import { AgButton } from 'src/components/ui';
import { useDebounce } from 'src/hooks/useDebounce';
import { PalletTag } from 'src/modules/palletTags';
import { appLinks } from 'src/routing/appLinks';
import { usePalletTagEditorSearch } from 'src/routing/routeSearch';
import CustomAssetManager from '../../../components/CustomAssetManager';
import CustomModal from '../../../components/CustomModal';
import { MAIN_BORDER_COLOR } from '../../../components/common';
import RightSidebar from '../../../components/RightSidebar';
import { default as stylesPlugin } from '../../../plugins/styles';
import PalletTagEditorSettings from '../../components/PalletTagEditorSettings';
import PalletTagStorageLoadModal from '../../components/PalletTagStorageLoadModal';
import PalletTagStorageSaveModal from '../../components/PalletTagStorageSaveModal';
import { default as componentPlugin } from '../../plugins/palletTagComponents';
import { NEW_TAG } from '../../plugins/palletTagStorage';
import { useBarcodeOrgInfoRef } from '../../plugins/useBarcodeOrgInfoRef';
import usePalletTagStorage from '../../plugins/usePalletTagStorage';

const gjsOptions: EditorConfig = {
  height: '100vh',
  storageManager: { type: 'hybrid', autosave: true, autoload: true },
  pageManager: { ...NEW_TAG },
};

export const testData = {
  lot: '00002-001',
  receiveDate: '12/19/23',
  countryOfOrigin: 'CAN',
  tagNumber: '00145',
  quantity: 13,
  description: 'Apple Idaho',
  packStyle: 'cartons Medium',
  productCode: '32912',
  size: 'Medium',
  grade: 'B',
  label: 'test',
  unit: 'cartons',
  gtinCode: '10054384002041',
  rawDate: '2021-09-24T00:00:00.000-04:00',
  commodity: 'Apple',
  variety: 'Idaho',
  organic: true,
  grower: 'Grower',
  vendor: 'Vendor',
  growerOrVendor: 'Mixed',
  fieldTicketId: '123456',
  harvestLocation: 'HL20233',
  weight: 12.34,
  weightPerUnit: 1.94,
  repackStartDate: '12/19/23',
  repackCompletedDate: '12/19/23',
  date: '12/19/23',
  customProductFields: { plu: 4072 },
  pickCode: { small: '00', large: '98' },
  repackStartDateMonthDay: 'Dec19',
  repackCompletedDateMonthDay: 'Dec19',
  dateMonthDay: 'Dec19',
  unitsPerPallet: 80,
};

const PalletTagEditorPage = ({ name }) => {
  const [tagSettings, setTagSettings] = useState({ width: '4', height: '2', padding: 0 });
  const orgInfo = useBarcodeOrgInfoRef();

  const [templateName, setTemplateName] = useState(name);
  const [storageSaveModalOpen, setStorageSaveModalOpen] = useState(false);
  const [storageLoadModalOpen, setStorageLoadModalOpen] = useState(false);
  const [_tagId, setTagId] = useState(null);

  const search = usePalletTagEditorSearch();
  const navigate = useNavigate();

  const _debounceNav = useDebounce(
    (variables) => {
      const encoded = encodeURIComponent(JSON.stringify(variables));

      void navigate(appLinks.palletTagEditor({ ...search, lookup: encoded }, { replace: true }));
    },
    500,
    null,
    [navigate, search]
  );

  const { plugin: storagePlugin } = usePalletTagStorage({
    tagConfig: { name: templateName, ...tagSettings },
    setStorageSaveModalOpen,
    setStorageLoadModalOpen,
    setTemplateId: setTagId,
  });

  return (
    <>
      <GjsEditor
        grapesjs={grapesjs}
        grapesjsCss="https://unpkg.com/grapesjs/dist/css/grapes.min.css"
        options={gjsOptions}
        plugins={[usePlugin(componentPlugin(orgInfo)), usePlugin(stylesPlugin), usePlugin(storagePlugin)]}
      >
        <PalletTagStorageSaveModal
          open={storageSaveModalOpen}
          onClose={() => setStorageSaveModalOpen(false)}
          setTemplateName={setTemplateName}
          templateName={templateName}
        />
        <PalletTagStorageLoadModal
          open={storageLoadModalOpen}
          onClose={() => setStorageLoadModalOpen(false)}
          setTemplateName={setTemplateName}
        />
        <div className={`flex h-full border-t ${MAIN_BORDER_COLOR}`}>
          <div className="gjs-column-m flex flex-col grow">
            <Box>
              <Paper sx={{ borderRadius: 0 }}>
                <WithEditor>
                  <PalletTagEditorSettings tagSettings={tagSettings} setTagSettings={setTagSettings} />
                </WithEditor>
              </Paper>
            </Box>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  width: `${tagSettings.width}in`,
                  maxHeight: `${tagSettings.height}in`,
                  overflow: 'hidden',
                  mt: 4,
                }}
              >
                <Canvas />
              </Box>
            </Box>
          </div>
          <RightSidebar className={`gjs-column-r w-[300px] border-l ${MAIN_BORDER_COLOR}`} />
        </div>
        <ModalProvider>
          {({ open, title, content, close }) => (
            <CustomModal open={open} title={title} close={close}>
              {content}
            </CustomModal>
          )}
        </ModalProvider>
        <AssetsProvider>
          {({ assets, select, close, Container }) => (
            <Container>
              <CustomAssetManager assets={assets} select={select} close={close} />
            </Container>
          )}
        </AssetsProvider>
        <WithEditor>
          <TagPreview width={tagSettings.width} height={tagSettings.height} padding={tagSettings.padding} />
        </WithEditor>
      </GjsEditor>
    </>
  );
};

const TagPreview = ({ width, height, padding }) => {
  const editor = useEditor();
  const [layout, setLayout] = useState({ html: editor.getHtml(), css: editor.getCss() });

  return (
    <Box sx={{ position: 'absolute', bottom: 0 }}>
      <AgButton
        variant="ghost"
        onPress={() => {
          setLayout({ html: editor.getHtml(), css: editor.getCss() });
        }}
      >
        Refresh
      </AgButton>
      <PalletTag
        template={{ version: 2, width: `${width}in`, height: `${height}in`, padding: `${padding}px`, layout }}
        id="test-tag"
        data={testData}
      />
    </Box>
  );
};

export default PalletTagEditorPage;
